export type GuideBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'images'; srcs: string[] }
  | { type: 'list'; items: string[] };

export type GuideStep = {
  number: number;
  title: string;
  blocks: GuideBlock[];
};

export type ParsedGuide = {
  introTitle: string;
  introQuote: string;
  steps: GuideStep[];
};

/** Map `images/foo.jpg` from the source doc to static `/onboarding/foo.jpg`. */
export function normalizeImageSrc(src: string): string {
  const s = src.trim();
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const name = s.replace(/^images\//, '').replace(/^\.\//, '');
  return `/onboarding/${name}`;
}

function parseStepBody(body: string): GuideBlock[] {
  const blocks: GuideBlock[] = [];
  const lines = body.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line || line === '---') {
      i++;
      continue;
    }

    const lineImgs = [...line.matchAll(/!\[([^\]]*)\]\(([^)]+)\)/g)];
    if (lineImgs.length > 0) {
      const srcs: string[] = [];
      while (i < lines.length) {
        const L = lines[i].trim();
        if (!L) {
          i++;
          continue;
        }
        const imgs = [...L.matchAll(/!\[([^\]]*)\]\(([^)]+)\)/g)];
        if (imgs.length === 0) break;
        for (const im of imgs) {
          srcs.push(normalizeImageSrc(im[2]));
        }
        i++;
      }
      blocks.push({ type: 'images', srcs });
      continue;
    }

    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length) {
        const L = lines[i].trim();
        if (!L) {
          i++;
          continue;
        }
        if (!L.startsWith('- ')) break;
        items.push(L.slice(2).trim());
        i++;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    let para = line.trim();
    i++;
    while (i < lines.length) {
      const next = lines[i];
      const nt = next.trim();
      if (!nt) break;
      if (next.match(/^\s*!\[/)) break;
      if (nt.startsWith('- ')) break;
      para += ' ' + nt;
      i++;
    }
    blocks.push({ type: 'paragraph', text: para });
  }

  return blocks;
}

export function parseAgencyOnboardingGuide(raw: string): ParsedGuide {
  const md = raw.replace(/\r\n/g, '\n');
  const titleMatch = md.match(/^#\s+(.+?)\s*$/m);
  const introTitle = titleMatch?.[1]?.trim() ?? 'Campaign workflow';

  let introQuote = '';
  const quoteBlock = md.match(/^#\s+.+\n\n>\s+([\s\S]+?)\n\n---\s*\n/m);
  if (quoteBlock) {
    introQuote = quoteBlock[1]
      .split('\n')
      .map((l) => l.replace(/^>\s?/, '').trim())
      .join(' ')
      .trim();
  }

  const stepRe = /^### Step (\d+):\s*(.+)$/gm;
  const matches: RegExpExecArray[] = [];
  let m: RegExpExecArray | null;
  while ((m = stepRe.exec(md)) !== null) {
    matches.push(m);
  }

  const steps: GuideStep[] = [];
  for (let idx = 0; idx < matches.length; idx++) {
    const match = matches[idx];
    const num = parseInt(match[1], 10);
    const title = match[2].trim();
    const start = match.index! + match[0].length;
    const end = idx + 1 < matches.length ? matches[idx + 1].index! : md.length;
    let body = md.slice(start, end).trim();
    body = body.replace(/\n---\s*$/g, '').trim();
    steps.push({
      number: num,
      title,
      blocks: parseStepBody(body),
    });
  }

  return { introTitle, introQuote, steps };
}
